import type { ReactNode } from 'react';

import { cn } from './cn';

/**
 * A committing action inside the content column — secondary by construction.
 *
 * The top bar's create action is a view's one primary button, so nothing in the
 * content column is filled. A destructive control is outline-only and takes the
 * danger colour on its label and border: `--danger` sits six degrees from
 * `--accent-text`, and at the same lightness a filled destructive button is
 * indistinguishable from «play».
 *
 * It lives in the shared ring because two areas draw it: the comment queue's
 * per-card actions and the case detail's three-decision row. It was the
 * moderation area's own until the decision moved onto the case surface, and a
 * second copy beside the first is how two buttons that must look identical stop
 * being identical.
 */
export function ColumnButton({
  onClick,
  tone = 'secondary',
  disabled = false,
  small = false,
  children,
}: {
  onClick: () => void;
  tone?: 'secondary' | 'danger' | 'ghost';
  disabled?: boolean;
  small?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'hit-target flex items-center justify-center gap-2 rounded-sm text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-45',
        small ? 'px-3' : 'px-4',
        tone === 'danger' && 'border border-danger text-danger hover:bg-danger-soft',
        tone === 'secondary' &&
          'border border-border-strong bg-surface-raised text-ink hover:bg-surface-overlay',
        tone === 'ghost' && 'text-ink-muted hover:bg-hover-bg hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}
